import React, { memo, useMemo, useRef } from "react";
import { Container } from "@pixi/react";
import { Settings, GAME_LAYOUT } from "../../../settings";
import OverlayReel from "../OverlayReel/OverlayReel";
import OverlaySymbol from "../OverlaySymbol/OverlaySymbol";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import { useSelector } from "react-redux";
import { selectHoldAndWinActive, selectFreeSpinActive } from "../../../State/selectors";
import { selectFreeSpinsActive } from "../../../State/Slices/freeSpinsSlice";

/**
 * FLAT overlay container for the base game.
 *
 * All 15 symbols (5 reels × 3 rows) are siblings in ONE sortable container, so
 * a symbol on reel 1 can render above one on reel 2 — impossible while each
 * reel owned its own 3 children, because PIXI only sorts among siblings.
 * Mirrors PotCollectionOverlayReelSet, which already works this way.
 *
 * OverlayReel still exists per reel: it owns that reel's OverlayReelController
 * and hosts the full-reel expanding wild spine. It just doesn't parent the
 * symbols anymore — it receives them as a ref slice.
 */
const OverlayReelSet = memo(({ symbolWidth, symbolHeight, reelSpacing }) => {
  const { overlayReelRefs } = useReelRefs();
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const freeSpinActive = useSelector(selectFreeSpinActive);
  // This game's bonus flag — see the note in ReelSet.jsx. `freeSpinActive` is
  // the old system's and is never raised here.
  const freeSpinsActive = useSelector(selectFreeSpinsActive);
  const symbolRefs = useRef(
    Array.from(
      { length: GAME_LAYOUT.REEL_COUNT },
      () => new Array(GAME_LAYOUT.SYMBOLS_VISIBLE),
    ),
  );

  // Memoize container Y position calculation
  const containerY = useMemo(
    () => -symbolHeight * (0.5 * Settings.SYMBOLS_VISIBLE - 0.5),
    [symbolHeight]
  );

  // ── `visible`, not alpha ──
  //
  // alpha 0.001 still RENDERS: PIXI skips a subtree only on visible === false,
  // renderable === false, or worldAlpha <= 0. This container holds one pooled
  // spine per symbol per cell — the heaviest layer in the game — so leaving it
  // at 0.001 drew all of it through the whole bonus round.
  //
  // Alpha is the right tool when a subtree must stay warm, because a texture
  // that goes 60s undrawn is unloaded by PIXI's texture GC and re-uploaded on
  // the frame it returns. That does not apply here: the bonus room's own pool
  // draws the very same symbol spines, from the same atlas pages, for the whole
  // round — so nothing this container needs ever goes idle.
  const containerVisible = useMemo(
    () => !holdAndWinActive && !freeSpinActive && !freeSpinsActive,
    [holdAndWinActive, freeSpinActive, freeSpinsActive]
  );

  // Pre-compute all 15 cell positions. x used to come from the reel container;
  // each symbol carries its own now. Both recompute on resize because they're
  // plain props derived from symbolHeight/reelSpacing.
  const symbolPositions = useMemo(() => {
    const positions = [];
    const centerOffset = (GAME_LAYOUT.REEL_COUNT - 1) / 2;
    for (let ri = 0; ri < GAME_LAYOUT.REEL_COUNT; ri++) {
      for (let si = 0; si < GAME_LAYOUT.SYMBOLS_VISIBLE; si++) {
        positions.push({
          reelIndex: ri,
          symbolIndex: si,
          x: reelSpacing * (ri - centerOffset),
          y: symbolHeight * si,
        });
      }
    }
    return positions;
  }, [reelSpacing, symbolHeight]);

  return (
    <Container
      name="spine_overlay"
      zIndex={5}
      y={containerY}
      visible={containerVisible}
      sortableChildren
    >
      {/* The 15 sortable cells. Emitted before the reels so their refs are
          attached by the time a reel's effect builds its controller. */}
      {symbolPositions.map(({ reelIndex, symbolIndex, x, y }) => (
        <OverlaySymbol
          key={`${reelIndex}_${symbolIndex}`}
          index={symbolIndex}
          reelIndex={reelIndex}
          x={x}
          y={y}
          ref={(el) => {
            symbolRefs.current[reelIndex][symbolIndex] = el;
          }}
        />
      ))}

      {/* Controller hosts — one per reel, carrying only the expanding wild. */}
      {GAME_LAYOUT.REEL_INDICES.map((index) => (
        <OverlayReel
          key={index}
          symbolHeight={symbolHeight}
          symbolWidth={symbolWidth}
          reelSpacing={reelSpacing}
          symbolRefs={symbolRefs.current[index]}
          ref={(el) => {
            overlayReelRefs.current[index] = el;
          }}
          index={index}
        />
      ))}
    </Container>
  );
});

export default OverlayReelSet;
